import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Loader2, 
  Save,
  Send,
  CheckCircle2,
  User,
  BookOpen,
  Briefcase,
  Target,
  Star
} from "lucide-react";

// Form Components
import ProfileSection from "@/components/assessment/ProfileSection";
import FoundationSection from "@/components/assessment/FoundationSection";
import CurrentStateSection from "@/components/assessment/CurrentStateSection";
import AspirationSection from "@/components/assessment/AspirationSection";
import SelfGradingSection from "@/components/assessment/SelfGradingSection";
import CertificationSection from "@/components/assessment/CertificationSection";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AssessmentPageContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [responses, setResponses] = useState({});
  const [progress, setProgress] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [totalAnswered, setTotalAnswered] = useState(0);

  // Refs for scrolling to sections
  const profileRef = useRef(null);
  const foundationRef = useRef(null);
  const currentStateRef = useRef(null);
  const aspirationRef = useRef(null);
  const selfGradingRef = useRef(null);
  const certificationRef = useRef(null);

  // Redirect to dynamic assessment form
  useEffect(() => {
    if (token) {
      navigate(`/dynamic-assessment/${token}`, { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchAssessment();
  }, [token]);

  // Update total answered count when responses change
  useEffect(() => {
    let count = 0;
    Object.values(responses).forEach(sectionResponses => {
      if (sectionResponses && typeof sectionResponses === 'object') {
        count += Object.keys(sectionResponses).filter(key => {
          const val = sectionResponses[key];
          return val !== null && val !== undefined && val !== '' && 
                 !(Array.isArray(val) && val.length === 0);
        }).length;
      }
    });
    setTotalAnswered(count);
  }, [responses]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessment/${token}`);
      
      if (!response.data.emailVerified) {
        navigate(`/assessment/${token}`);
        return;
      }
      
      setProfile(response.data.profile);
      setResponses(response.data.existingResponses || {});
      setProgress(response.data.progressPercentage || 0);
    } catch (err) {
      console.error("Error fetching assessment:", err);
      toast.error("Failed to load assessment");
      navigate(`/assessment/${token}`);
    } finally {
      setLoading(false);
    }
  };

  const saveResponse = useCallback(async (sectionId, questionId, value) => {
    // Update local state immediately
    setResponses(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [questionId]: value
      }
    }));

    // Save to backend
    try {
      setSaving(true);
      const response = await axios.post(`${API}/assessment/${token}/save`, {
        section_id: sectionId,
        question_id: questionId,
        response: value
      });
      setProgress(response.data.progress);
      setLastSaved(new Date());
    } catch (err) {
      console.error("Error saving response:", err);
      toast.error("Failed to save response");
    } finally {
      setSaving(false);
    }
  }, [token]);

  const saveAndExit = useCallback(async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/assessment/${token}/save-progress`);
      toast.success("Progress saved! You can close this page and return later.");
      setLastSaved(new Date());
    } catch (err) {
      console.error("Error saving progress:", err);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  }, [token]);

  const handleSubmit = async (certification) => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/assessment/${token}/submit`, {
        certification
      });
      toast.success("Assessment submitted successfully!");
      navigate(`/assessment/${token}/confirmation`);
    } catch (err) {
      console.error("Error submitting assessment:", err);
      toast.error(err.response?.data?.detail || "Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#003366] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm py-3 px-4" data-testid="assessment-header">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-[#003366] font-['Manrope']">
                Technology Assessment
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Save Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={saveAndExit}
              disabled={saving}
              className="gap-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white"
              data-testid="save-progress-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Save Progress</span>
            </Button>

            {/* Progress & Status */}
            <div className="flex items-center gap-3">
              <div className="hidden md:flex flex-col items-end text-right">
                <span className="text-xs text-muted-foreground">Answered</span>
                <span className="text-sm font-semibold text-[#003366]">{totalAnswered} / 69</span>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={progress} className="w-20 h-2" data-testid="progress-bar" />
                <span className="text-sm font-medium text-[#003366]">{progress}%</span>
              </div>
              {saving && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
              {!saving && lastSaved && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Saved
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Manager Info Bar */}
      {profile?.managerName && (
        <div className="bg-[#f0f7ff] border-b border-[#003366]/20 py-2 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-sm">
            <User className="w-4 h-4 text-[#003366]" />
            <span className="text-[#003366]">
              <span className="text-muted-foreground">Your Manager:</span>{" "}
              <span className="font-semibold">{profile.managerName}</span>
              {profile.managerTitle && <span className="text-muted-foreground"> — {profile.managerTitle}</span>}
            </span>
          </div>
        </div>
      )}

      {/* Quick Navigation */}
      <div className="sticky top-[65px] z-40 bg-slate-100 border-b py-2 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Jump to:</span>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(profileRef)}>
            <User className="w-3 h-3 mr-1" /> Profile
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(foundationRef)}>
            <BookOpen className="w-3 h-3 mr-1" /> Foundation
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(currentStateRef)}>
            <Briefcase className="w-3 h-3 mr-1" /> Current State
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(aspirationRef)}>
            <Target className="w-3 h-3 mr-1" /> Aspiration
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(selfGradingRef)}>
            <Star className="w-3 h-3 mr-1" /> Self-Grading
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => scrollToSection(certificationRef)}>
            <CheckCircle2 className="w-3 h-3 mr-1" /> Submit
          </Button>
        </div>
      </div>

      {/* Main Content - Single Scrollable Page */}
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-12">
        
        {/* Profile Section */}
        <section ref={profileRef} id="profile" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Your Profile</h2>
              <p className="text-sm text-muted-foreground">Verify your information</p>
            </div>
          </div>
          <ProfileSection profile={profile} />
        </section>

        <hr className="border-slate-200" />

        {/* Section 1: Foundation */}
        <section ref={foundationRef} id="foundation" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Section 1: FOUNDATION</h2>
              <p className="text-sm text-muted-foreground">Background & Credentials</p>
            </div>
          </div>
          <FoundationSection 
            responses={responses['foundation'] || {}}
            onSave={(questionId, value) => saveResponse('foundation', questionId, value)}
            profile={profile}
          />
        </section>

        <hr className="border-slate-200" />

        {/* Section 2: Current State */}
        <section ref={currentStateRef} id="current-state" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Section 2: CURRENT STATE</h2>
              <p className="text-sm text-muted-foreground">Skills & Experience Today</p>
            </div>
          </div>
          <CurrentStateSection 
            responses={responses['current-state'] || {}}
            onSave={(questionId, value) => saveResponse('current-state', questionId, value)}
            profile={profile}
          />
        </section>

        <hr className="border-slate-200" />

        {/* Section 3: Aspiration */}
        <section ref={aspirationRef} id="aspiration" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Section 3: ASPIRATION</h2>
              <p className="text-sm text-muted-foreground">Goals & Growth Areas</p>
            </div>
          </div>
          <AspirationSection 
            responses={responses['aspiration'] || {}}
            onSave={(questionId, value) => saveResponse('aspiration', questionId, value)}
            profile={profile}
          />
        </section>

        <hr className="border-slate-200" />

        {/* Section 4: Self-Grading */}
        <section ref={selfGradingRef} id="self-grading" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#B8860B] flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Section 4: SELF-GRADING</h2>
              <p className="text-sm text-muted-foreground">Rate your own capabilities honestly</p>
            </div>
          </div>
          <SelfGradingSection 
            responses={responses['self-grading'] || {}}
            onSave={(questionId, value) => saveResponse('self-grading', questionId, value)}
            profile={profile}
          />
        </section>

        <hr className="border-slate-200" />

        {/* Certification & Submit */}
        <section ref={certificationRef} id="certification" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#B8860B] flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#003366] font-['Manrope']">Certification & Submit</h2>
              <p className="text-sm text-muted-foreground">Review and submit your assessment</p>
            </div>
          </div>
          <CertificationSection 
            profile={profile}
            onSubmit={handleSubmit}
            submitting={submitting}
            progress={progress}
            totalAnswered={totalAnswered}
          />
        </section>

        {/* Bottom spacer */}
        <div className="h-20" />
      </main>

      {/* Floating Save Button for Mobile */}
      <div className="fixed bottom-6 right-6 sm:hidden">
        <Button
          size="lg"
          onClick={saveAndExit}
          disabled={saving}
          className="rounded-full w-14 h-14 bg-[#003366] hover:bg-[#002244] shadow-lg"
        >
          {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Save className="w-6 h-6" />}
        </Button>
      </div>
    </div>
  );
}

// Export wrapped with MaintenanceOverlay
export default function AssessmentPage() {
  const { token } = useParams();
  
  return (
    <MaintenanceOverlay assessmentToken={token}>
      <AssessmentPageContent />
    </MaintenanceOverlay>
  );
}
